import { useCallback, useContext } from "react";

import { identifyUserToMixpanel } from "@/utils/Analytics/Mixpanel/identifyUserToMixpanel/identifyUserToMixpanel";
import {
  setAnalyticsTrackingAllowed,
  shouldAllowAnalyticsTracking,
} from "@/utils/Analytics/analyticsTrackingConsent";
import LaunchDarklyContext, {
  LDUser,
} from "@/utils/launchDarkly/launchDarklyProvider";
import { initializeSentry } from "@/utils/sentry/initializeSentry";
import { identifySentryUser } from "@whoosh/utilities/src/Sentry/identifySentryUser";

import { whooshPatronUserIdentityForExternalTools } from "./types";

/**
 * This handles identifying our user with all of our external tools.
 *
 * This includes
 * Sentry
 * Mixpanel
 * LaunchDarkly
 *
 * This should allow us to ensure they all stay in sync as needed in the various locations
 * we update our user
 */
export const useIdentifyUserToExternalTools = () => {
  const { identifyNewUser: identifyLaunchDarklyUser } =
    useContext(LaunchDarklyContext);
  const handleIdentifyUsers = useCallback(
    ({
      user,
      currentClub,
    }: {
      user: whooshPatronUserIdentityForExternalTools;
      currentClub: { id: string; name: string };
    }) => {
      const shouldTrack =
        !!user.userId &&
        shouldAllowAnalyticsTracking({
          requiresAnalyticsTrackingConsent:
            user.requiresAnalyticsTrackingConsent,
          analyticsTrackingConsentedAt: user.analyticsTrackingConsentedAt,
        });
      setAnalyticsTrackingAllowed(shouldTrack);

      const launchDarklyUser: LDUser = {
        kind: "user",
        key: user.userId?.length ? user.userId : "unknown-user",
        name: user.name?.length ? user.name : "unknown-user",
        email: user.email,
        clubId: currentClub.id,
        clubName: currentClub.name,
        custom: {
          clubId: currentClub.id,
          clubName: currentClub.name,
          facilityId: currentClub.id, // for backwards compatibility
          facilityName: currentClub.name, // for backwards compatibility
        },
      };
      identifyLaunchDarklyUser(launchDarklyUser);

      if (!shouldTrack) {
        return;
      }

      initializeSentry();
      identifyUserToMixpanel(user);
      identifySentryUser({
        clubName: currentClub.name,
        identity: { email: user.email || "", username: user.name || "" },
      });
    },
    [identifyLaunchDarklyUser]
  );
  return handleIdentifyUsers;
};
